/**
 * A master–detail left rail, in the Agents tab's idiom (task 810).
 *
 * The Agents subtab solved a shape its neighbours have too: several sections
 * that are each long enough to bury the ones beneath them. Its answer is a rail
 * of cards on the left and one section's content on the right. This is that
 * rail with the agent-specific parts — the live dot, the resolved model line,
 * the flow position — taken out, so a second subtab can wear the same clothes.
 * The two reading as ONE design is the whole point of reusing the idiom rather
 * than inventing a second list style beside it.
 *
 * Deliberately NOT a generalization of `AgentRailCard`: that card resolves an
 * agent, its model through the engine-mirroring cascade, and its active state,
 * and folding a "sometimes an agent, sometimes a schema" mode into it would
 * make both harder to read to save about thirty lines. What must not drift is
 * the CHROME — the card's border, padding, icon chip and selected treatment —
 * and that is what is shared here, in one place either rail's edit has to pass
 * through.
 */
import type { ComponentType, ReactNode, SVGProps } from 'react';

export interface SectionRailEntry {
  id: string;
  label: string;
  /** The one line under the label: what is in this section, counted. */
  summary: string;
  icon: ComponentType<SVGProps<SVGSVGElement>>;
}

interface SectionRailProps {
  /** The small uppercase line above the cards, as the Agents rail has. */
  heading: string;
  /**
   * Optional control on the heading row, inline right after the heading text
   * (task 1195: the Testing rail's "How testing works" ?; task 1197: beside the
   * word, not at the rail's right edge). Omitted, the heading renders exactly as
   * it always has.
   */
  headingAction?: ReactNode;
  entries: SectionRailEntry[];
  selectedId: string;
  onSelect: (id: string) => void;
  /** Distinguishes this rail's test ids when a page carries more than one. */
  testId?: string;
  ariaLabel: string;
}

export function SectionRail({
  heading,
  headingAction,
  entries,
  selectedId,
  onSelect,
  testId = 'section-rail',
  ariaLabel,
}: SectionRailProps) {
  return (
    <nav aria-label={ariaLabel} data-testid={testId} className="flex flex-col gap-2">
      {headingAction ? (
        <div data-testid={`${testId}-heading`} className="inline-flex items-center gap-1.5 px-0.5">
          <p className="text-[11px] font-bold uppercase tracking-wider text-gray-500 dark:text-gray-400">{heading}</p>
          <span data-testid={`${testId}-heading-action`} className="flex shrink-0 items-center">
            {headingAction}
          </span>
        </div>
      ) : (
        <p
          data-testid={`${testId}-heading`}
          className="px-0.5 text-[11px] font-bold uppercase tracking-wider text-gray-500 dark:text-gray-400"
        >
          {heading}
        </p>
      )}
      {entries.map((entry) => {
        const selected = entry.id === selectedId;
        const Icon = entry.icon;
        return (
          <button
            key={entry.id}
            type="button"
            onClick={() => onSelect(entry.id)}
            aria-current={selected ? 'true' : undefined}
            data-testid={`${testId}-card-${entry.id}`}
            data-selected={selected ? 'true' : 'false'}
            className={`flex w-full items-start gap-3 rounded-lg border p-3 text-left transition-colors ${
              selected
                ? 'border-indigo-500 bg-indigo-50 dark:border-indigo-400 dark:bg-indigo-900/30'
                : 'border-gray-200 bg-white hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-800 dark:hover:bg-gray-700/50'
            }`}
          >
            <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-indigo-100 dark:bg-indigo-900/50">
              <Icon className="h-4 w-4 text-indigo-600 dark:text-indigo-300" />
            </span>
            <span className="min-w-0 flex-1">
              <span className="block truncate text-sm font-semibold text-gray-900 dark:text-white">
                {entry.label}
              </span>
              <span className="mt-0.5 block truncate text-[11px] text-gray-500 dark:text-gray-400">
                {entry.summary}
              </span>
            </span>
          </button>
        );
      })}
    </nav>
  );
}

export default SectionRail;
